import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './ToolsConfigTab.module.css';
import { useToolsStore } from '../../../../../store/toolsStore';
import { useConnectionsStore } from '../../../../../store/connectionsStore';
import ToolPicker from '../../../../../ui/ToolPicker/ToolPicker';
import WebchatToolsFilters from './WebchatToolsFilters';
import type { ToolOption } from '../../../../../types/types';

export interface ToolsConfigValues {
  allowedTools: number[];
  dangerousTools: number[];
  maxToolIterations: number;
}

interface Props {
  config: ToolsConfigValues;
  onChange: (partial: Partial<ToolsConfigValues>) => void;
}

export default function ToolsConfigTab({ config, onChange }: Props) {
  const { tools, getTools, setTools } = useToolsStore();
  const { connectors, getBasicConnectors } = useConnectionsStore();
  const loading = tools.length === 0;

  const [filteredToolOptions, setFilteredToolOptions] = useState<ToolOption[]>([]);

  useEffect(() => {
    if (tools.length === 0) {
      getTools().then((data: any) => setTools(data.tools || data)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (connectors.length === 0) getBasicConnectors();
  }, []);

  const connectorMap = Object.fromEntries(connectors.map(c => [c.id, c.connectionName]));

  // Memoized: WebchatToolsFilters runs an effect on `options` that sets state in this component,
  // so a new array identity every render would cause an infinite render loop.
  const toolOptions: ToolOption[] = useMemo(
    () =>
      tools
        .filter(t => t.enabled && (t.mainConnectionId ? connectors.some(c => c.id === t.mainConnectionId && c.isActive) : true))
        .map(t => ({
          id: String(t.id),
          name: t.title || t.name,
          description: t.description,
          connectorId: t.mainConnectionId,
          connectorName: t.mainConnectionId ? connectorMap[t.mainConnectionId] : undefined,
          environment: t.environment,
          httpMethod: t.httpMethod,
          tags: t.tags?.map(tag => tag.name),
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tools, connectors],
  );

  const handleFilter = useCallback((filtered: ToolOption[]) => {
    setFilteredToolOptions(filtered);
  }, []);

  const toSelectedFromIds = (ids: number[]): ToolOption[] =>
    ids
      .map(id => {
        const tool = tools.find(t => t.id === id);
        return {
          id: String(id),
          name: tool?.title || tool?.name || `Tool #${id}`,
          description: tool?.description,
          connectorId: tool?.mainConnectionId,
          connectorName: tool?.mainConnectionId ? connectorMap[tool.mainConnectionId] : undefined,
          environment: tool?.environment,
          httpMethod: tool?.httpMethod,
          tags: tool?.tags?.map(tag => tag.name),
        };
      })
      .filter(o => toolOptions.some(a => a.id === o.id));

  const dangerousSet = new Set(config.dangerousTools.map(String));
  const allowedSet = new Set(config.allowedTools.map(String));

  const allowedToolOptions = filteredToolOptions.filter(t => !dangerousSet.has(t.id));
  const dangerousToolOptions = filteredToolOptions.filter(t => !allowedSet.has(t.id));

  if (loading) {
    return (
      <div className={styles.section}>
        <p style={{ color: '#6b7280' }}>Loading tools...</p>
      </div>
    );
  }

  return (
    <>
      <WebchatToolsFilters options={toolOptions} onFilter={handleFilter} />

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Allowed Tools</h3>
        <div className={styles.field}>
          <label>
            Select which tools are available in webchat.
            <span className={styles.passSetHint}> Leave empty to allow no tools.</span>
          </label>
          <ToolPicker
            options={allowedToolOptions}
            selected={toSelectedFromIds(config.allowedTools)}
            onChange={sel => onChange({ allowedTools: sel.map(s => Number(s.id)) })}
            placeholder="Search tools..."
          />
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Need Approval Tools</h3>
        <div className={styles.field}>
          <label>Tools that require user confirmation before execution.</label>
          <ToolPicker
            options={dangerousToolOptions}
            selected={toSelectedFromIds(config.dangerousTools)}
            onChange={sel => onChange({ dangerousTools: sel.map(s => Number(s.id)) })}
            placeholder="Search tools..."
          />
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Execution Limits</h3>
        <div className={styles.field}>
          <label>Max Tool Iterations</label>
          <input
            type="number"
            value={config.maxToolIterations}
            min={1}
            max={50}
            onChange={e => onChange({ maxToolIterations: e.target.value === '' ? ('' as unknown as number) : Math.min(50, Math.max(1, parseInt(e.target.value, 10))) })}
          />
          <span className={styles.passSetHint}>Maximum number of tool-calling iterations per chat request.</span>
        </div>
      </div>
    </>
  );
}
